import { useState, useMemo, useCallback } from 'react';
import { useAuth } from '@frontegg/react';
import {
  HistoryOutlined,
  InboxOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { Upload } from 'antd';
import {
  Drawer,
  Steps,
  Table,
  Button,
  toast,
  Alert,
  type TableProps,
  type DrawerProps,
} from '@insight-health-ai/roseate/components';
import { CheckCircle, ReverseLeft } from '@insight-health-ai/roseate/icons';
import {
  useGetCurrentPractitioner,
  useGetOrganizationSettings,
} from 'src/hooks/settings';
import {
  useSyncAppointments,
  useUploadAppointmentCSV,
} from 'src/hooks/appointment';
import { analytics, api } from 'src/services';
import {
  CSVParsedRow,
  BulkUploadSettings,
  CSV_SCHEMA_COLUMNS,
  COLUMNS_TO_HIDE_IF_NOT_PRESENT,
} from './types';
import {
  parseFile,
  validateParsedData,
  transformToSyncPayload,
  generateS3Key,
  fileToBase64,
} from './utils';

type AppointmentBulkUploadDrawerProps = {
  open: boolean;
  onClose: () => void;
  onViewHistory: () => void;
};

export const AppointmentBulkUploadDrawer = ({
  open,
  onClose,
  onViewHistory,
}: AppointmentBulkUploadDrawerProps) => {
  const { user } = useAuth();

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [csvData, setCsvData] = useState<CSVParsedRow[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [missingColumns, setMissingColumns] = useState<string[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  const { data: currentPractitioner, isLoading: isCurrentPractitionerLoading } =
    useGetCurrentPractitioner();

  const {
    data: organizationSettingsData,
    isLoading: isOrganizationSettingsLoading,
  } = useGetOrganizationSettings<BulkUploadSettings>(
    {
      key: 'appt_bulk_upload_settings',
      orgUuid: currentPractitioner?.org_uuid!,
    },
    {
      enabled: !!currentPractitioner?.org_uuid,
    }
  );

  const { mutateAsync: syncAppointments } = useSyncAppointments();
  const { mutateAsync: uploadCSV } = useUploadAppointmentCSV();

  const isLoading =
    isCurrentPractitionerLoading || isOrganizationSettingsLoading;

  const settings = organizationSettingsData?.settings;

  const resetState = useCallback(() => {
    setCurrentStep(0);
    setCsvData([]);
    setFile(null);
    setMissingColumns([]);
    setFileError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleFileUpload = useCallback(
    async (uploadedFile: File) => {
      try {
        setFileError(null);
        setMissingColumns([]);

        const fileType = uploadedFile.type === 'text/csv' ? 'CSV' : 'Excel';
        analytics.track(
          `Selected A ${fileType} File For Bulk Upload Appointments`,
          { email: user?.email }
        );

        setFile(uploadedFile);

        const { enabled: dacEnabled } = await api
          .get<{ enabled: boolean }>('/v2/dac/enabled')
          .then((res) => res.data);

        const parsedData = await parseFile(uploadedFile, settings, dacEnabled);
        const validation = validateParsedData(parsedData);

        if (!validation.valid) {
          setMissingColumns(validation.missingColumns);
        }

        setCsvData(parsedData);
        setCurrentStep(1);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error occurred';
        setFileError(errorMessage);
      }
    },
    [settings, user?.email]
  );

  const handleSubmit = useCallback(async () => {
    if (!file) return;

    analytics.track('Submitted CSV File For Bulk Upload Appointments', {
      email: user?.email,
    });

    try {
      const appointments = transformToSyncPayload(csvData);
      const s3Key = generateS3Key(file.name);

      const syncPayload = {
        s3_key: s3Key,
        appointments,
      };

      await syncAppointments(syncPayload);

      toast.success('Uploading file...');
      const base64File = await fileToBase64(file);
      await uploadCSV({
        s3_key: s3Key,
        body: base64File,
        content_type: file.type,
      });

      analytics.track(
        'Successfully Uploaded CSV File For Bulk Upload Appointments',
        { email: user?.email }
      );

      toast.success('File uploaded successfully');
      setCurrentStep(2);
    } catch (error) {
      analytics.track(
        'Failed To Calculate Time Needed To Upload CSV File For Bulk Upload Appointments',
        { email: user?.email }
      );
      toast.error('Error uploading appointments');
    }
  }, [file, csvData, user?.email, syncAppointments, uploadCSV]);

  const columns = useMemo((): TableProps<CSVParsedRow>['columns'] => {
    if (!csvData || csvData.length === 0) {
      return [];
    }

    const firstRow = csvData[0];

    return CSV_SCHEMA_COLUMNS.map((column) => {
      if (
        COLUMNS_TO_HIDE_IF_NOT_PRESENT.includes(column.key) &&
        firstRow[column.key] === undefined
      ) {
        return null;
      }
      return {
        title: column.name === 'Sex' ? 'Gender' : column.name,
        dataIndex: column.key,
        key: column.key,
        render: (value: unknown) => {
          if (value === null || value === undefined) {
            return <span className="text-gray-400">-</span>;
          }
          return (
            <span
              className={
                column.name.toLowerCase().includes('date') ? 'text-nowrap' : ''
              }
            >
              {String(value)}
            </span>
          );
        },
      };
    }).filter(Boolean) as NonNullable<TableProps<CSVParsedRow>['columns']>;
  }, [csvData]);

  const renderUploadStep = () => {
    if (fileError) {
      return (
        <div className="flex justify-center items-center h-64 mt-12">
          <Alert
            appearance="error"
            message="Error Processing File"
            description={
              <div className="flex flex-col gap-2">
                {fileError && <p>{fileError}</p>}
                <p className="font-medium">
                  Please contact{' '}
                  <a
                    href="mailto:help@insighthealth.ai"
                    className="text-primary underline"
                  >
                    help@insighthealth.ai
                  </a>{' '}
                  for support.
                </p>
              </div>
            }
          />
        </div>
      );
    }

    return (
      <Upload.Dragger
        accept=".csv,.xlsx,.xls"
        fileList={[]}
        beforeUpload={(uploadedFile) => {
          handleFileUpload(uploadedFile as unknown as File);
          return false;
        }}
        height={250}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text text-primary">
          Click or drag CSV, XLSX, or XLS file to this area to upload
        </p>
      </Upload.Dragger>
    );
  };

  const renderPreviewStep = () => {
    if (missingColumns.length > 0) {
      return (
        <div className="flex justify-center items-center h-64 mt-12">
          <Alert
            appearance="error"
            message="Uploaded file is missing the following required column(s)"
            description={
              <div className="flex flex-col gap-4">
                <ul className="list-disc pl-5">
                  {missingColumns.map((column, index) => (
                    <li key={index}>{column}</li>
                  ))}
                </ul>
                <p className="font-medium">
                  Please contact{' '}
                  <a
                    href="mailto:help@insighthealth.ai"
                    className="text-primary underline"
                  >
                    help@insighthealth.ai
                  </a>{' '}
                  for support.
                </p>
              </div>
            }
          />
        </div>
      );
    }

    return <Table data={csvData} columns={columns} pagination={false} />;
  };

  const renderResultStep = () => {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <CheckCircle className="text-6xl text-success" />
        <h2 className="text-xl font-semibold">Upload Complete</h2>
        <p className="text-gray-600">
          An email summary will be sent to you shortly.
        </p>
        <Button appearance="primary" onClick={handleClose}>
          Done
        </Button>
      </div>
    );
  };

  const steps: {
    title: string;
    content: React.ReactNode;
    drawerProps?: Omit<DrawerProps, 'open' | 'onClose'>;
  }[] = [
    {
      title: 'Upload File',
      content: renderUploadStep(),
    },
    {
      title: 'Preview Changes',
      content: renderPreviewStep(),
      drawerProps: {
        extras: (
          <span className="text-sm text-gray-600">
            Total Rows: {csvData.length}
          </span>
        ),
        primaryAction: {
          label: 'Submit',
          icon: <SaveOutlined />,
          onClick: handleSubmit,
        },
        secondaryAction: {
          label: 'Retry',
          icon: <ReverseLeft />,
          onClick: resetState,
        },
      },
    },
    {
      title: 'Result',
      content: renderResultStep(),
    },
  ];
  const currentStepData = steps[currentStep];

  return (
    <Drawer
      size="xl"
      loading={isLoading}
      className="insight-background"
      title="Bulk Upload Appointments"
      {...(currentStepData.drawerProps ?? {})}
      open={open}
      onClose={handleClose}
      headerActions={{
        list: [
          {
            label: 'View History',
            icon: <HistoryOutlined />,
            onClick: onViewHistory,
            appearance: 'secondary',
            variant: 'outlined',
          },
        ],
      }}
    >
      <div className="flex flex-col gap-6">
        <Steps
          current={currentStep}
          items={[
            { title: 'Upload File' },
            { title: 'Preview Changes' },
            { title: 'Result' },
          ]}
        />
        {currentStepData.content}
      </div>
    </Drawer>
  );
};
